import React from 'react';
import { Search, Ban, UserCheck, UserPlus, UserMinus, X, ChevronLeft, ChevronRight } from 'lucide-react';

const UsersTab = ({
  users,
  myApprovers,
  commissionRates,
  forms,
  setForms,
  searchUsers,
  setConfirmations,
  handleAction,
  loading,
  language,
  t,
  isApproverTab = false,
  pagination,
  onPageChange
}) => {
  if (isApproverTab) {
    return <ApproversTab
      myApprovers={myApprovers}
      commissionRates={commissionRates}
      forms={forms}
      setForms={setForms}
      searchUsers={searchUsers}
      handleAction={handleAction}
      setConfirmations={setConfirmations}
      loading={loading}
      language={language}
      t={t}
    />;
  }

  const selectedUser = forms.userSearch?.selectedUser;

  return (
    <div className="role-management">
      <div className="role-management-section">
        <h4 className="role-management-title">{t('userRoleManagement', language)}</h4>
        <div className="role-search-section">
          <div className="role-search-container">
            <Search className="role-search-icon" />
            <input
              type="text"
              placeholder={t('searchUsers', language) + '...'}
              value={forms.userSearch.searchTerm}
              onChange={(e) => {
                setForms(prev => ({
                  ...prev,
                  userSearch: {
                    ...prev.userSearch,
                    searchTerm: e.target.value
                  }
                }));
                searchUsers(e.target.value, 'userSearch');
              }}
              className="role-search-input"
            />
          </div>
          {forms.userSearch.searchResults.length > 0 && (
            <div className="role-search-results">
              {forms.userSearch.searchResults.map(searchUser => (
                <div
                  key={searchUser._id}
                  onClick={() => setForms(prev => ({
                    ...prev,
                    userSearch: {
                      ...prev.userSearch,
                      selectedUser: searchUser,
                      searchResults: [],
                      searchTerm: ''
                    }
                  }))}
                  className="role-search-result-item"
                  style={{ cursor: 'pointer' }}
                >
                  <p className="role-search-result-name">{searchUser.inGameName}</p>
                  <p className="role-search-result-email">{searchUser.email}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {selectedUser && (
          <div className="selected-user-card" style={{
            marginTop: '15px',
            border: '2px solid #ffd700',
            background: 'rgba(255, 215, 0, 0.1)'
          }}>
            <div className="role-search-result-content">
              <div className="role-search-result-info" style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h5 className="role-search-result-name" style={{ color: '#ffd700', fontSize: '18px' }}>
                    {selectedUser.username}
                  </h5>
                  <button
                    onClick={() => setForms(prev => ({
                      ...prev,
                      userSearch: { ...prev.userSearch, selectedUser: null }
                    }))}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#888' }}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <p className="role-search-result-email">
                  {selectedUser.email} • {selectedUser.inGameName} • {selectedUser.points?.toLocaleString() || 0} {t('pts', language)}
                </p>
                <p className="role-search-result-stats">
                  {t('joinDate', language) || 'Join Date'}: {selectedUser.createdAt ? new Date(selectedUser.createdAt).toLocaleDateString() : 'N/A'} • 
                  {t('totalActiveTime', language) || 'Total Active'}: {selectedUser.totalActiveMinutes ? (selectedUser.totalActiveMinutes / 60).toFixed(1) + 'h' : '0h'} • 
                  {t('avgDailyTime', language) || 'Avg Daily'}: {selectedUser.createdAt && selectedUser.totalActiveMinutes ? (selectedUser.totalActiveMinutes / Math.max(1, Math.ceil((Date.now() - new Date(selectedUser.createdAt).getTime()) / 86400000))).toFixed(0) + 'm' : '0m'}
                </p>
                <p className="role-search-result-stats">
                  {selectedUser.role} • {selectedUser.wins || 0}W-{selectedUser.losses || 0}L
                  {selectedUser.banned && <span style={{ color: '#ff4444', marginLeft: '10px' }}>• BANNED</span>}
                </p>
              </div>
              <div className="modal-actions" style={{ display: 'flex', gap: '10px' }}>
                {!selectedUser.banned ? (
                  <button
                    onClick={() => setConfirmations(prev => ({ ...prev, ban: { show: true, user: selectedUser } }))}
                    className="btn-compact btn-danger"
                  >
                    <Ban className="w-4 h-4" />
                    {t('ban', language)}
                  </button>
                ) : (
                  <button
                    onClick={() => setConfirmations(prev => ({ ...prev, unban: { show: true, user: selectedUser } }))}
                    className="btn-compact btn-success"
                  >
                    <UserCheck className="w-4 h-4" />
                    {t('unban', language)}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="role-guide">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <h4 className="role-guide-title" style={{ margin: 0 }}>
            {t('allUsers', language)} ({pagination?.total || users.length})
          </h4>
          {pagination && pagination.pages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button
                onClick={() => onPageChange && onPageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
                style={{
                  background: pagination.page <= 1 ? '#333' : '#ffd700',
                  color: pagination.page <= 1 ? '#666' : '#000',
                  border: 'none', borderRadius: '5px', padding: '5px 10px',
                  cursor: pagination.page <= 1 ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center'
                }}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span style={{ color: '#fff', fontSize: '14px' }}>
                {pagination.page} / {pagination.pages}
              </span>
              <button
                onClick={() => onPageChange && onPageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
                style={{
                  background: pagination.page >= pagination.pages ? '#333' : '#ffd700',
                  color: pagination.page >= pagination.pages ? '#666' : '#000',
                  border: 'none', borderRadius: '5px', padding: '5px 10px',
                  cursor: pagination.page >= pagination.pages ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center'
                }}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
        <div className="role-search-section">
          {users.map(u => (
            <div
              key={u._id}
              className={`selected-user-card ${u.banned ? 'border-red-600 bg-red-900' : ''}`}
              style={u.banned ? { borderColor: '#dc2626', background: 'rgba(220, 38, 38, 0.2)' } : {}}
            >
              <div className="role-search-result-content">
                <div className="role-search-result-info">
                  <h5 className="role-search-result-name">{u.username}</h5>
                  <p className="role-search-result-email">
                    {u.email} • {u.inGameName} • {u.points?.toLocaleString() || 0} {t('pts', language)}
                  </p>
                  <p className="role-search-result-stats" style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
                    {t('joinDate', language) || 'Join Date'}: {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : 'N/A'} • 
                    {t('totalActiveTime', language) || 'Total Active'}: {u.totalActiveMinutes ? (u.totalActiveMinutes / 60).toFixed(1) + 'h' : '0h'} • 
                    {t('avgDailyTime', language) || 'Avg Daily'}: {u.createdAt && u.totalActiveMinutes ? (u.totalActiveMinutes / Math.max(1, Math.ceil((Date.now() - new Date(u.createdAt).getTime()) / 86400000))).toFixed(0) + 'm' : '0m'}
                  </p>
                  <p className="role-search-result-stats">
                    {u.role} • {u.wins || 0}W-{u.losses || 0}L
                  </p>
                </div>
                <div className="modal-actions">
                  {!u.banned ? (
                    <button
                      onClick={() => setConfirmations(prev => ({ ...prev, ban: { show: true, user: u } }))}
                      className="btn-compact btn-danger"
                    >
                      <Ban className="w-4 h-4" />
                      {t('ban', language)}
                    </button>
                  ) : (
                    <button
                      onClick={() => setConfirmations(prev => ({ ...prev, unban: { show: true, user: u } }))}
                      className="btn-compact btn-success"
                    >
                      <UserCheck className="w-4 h-4" />
                      {t('unban', language)}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {pagination && pagination.pages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '15px' }}>
            <button
              onClick={() => onPageChange && onPageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
              style={{
                background: pagination.page <= 1 ? '#333' : '#ffd700',
                color: pagination.page <= 1 ? '#666' : '#000',
                border: 'none', borderRadius: '5px', padding: '8px 15px',
                cursor: pagination.page <= 1 ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', gap: '5px'
              }}
            >
              <ChevronLeft className="w-4 h-4" /> Previous
            </button>
            <span style={{ color: '#fff', fontSize: '14px' }}>
              Page {pagination.page} of {pagination.pages}
            </span>
            <button
              onClick={() => onPageChange && onPageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.pages}
              style={{
                background: pagination.page >= pagination.pages ? '#333' : '#ffd700',
                color: pagination.page >= pagination.pages ? '#666' : '#000',
                border: 'none', borderRadius: '5px', padding: '8px 15px',
                cursor: pagination.page >= pagination.pages ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', gap: '5px'
              }}
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const ApproversTab = ({
  myApprovers,
  commissionRates,
  setConfirmations,
  language,
  t
}) => (
  <div className="role-management">
    <div className="role-guide">
      <h4 className="role-guide-title">{t('myApprovers', language)} ({myApprovers.length})</h4>
      {myApprovers.length === 0 ? (
        <div className="role-placeholder">
          <UserPlus className="role-placeholder-icon" />
          <p className="role-placeholder-text">{t('noApproversAdded', language)}</p>
        </div>
      ) : (
        <div className="role-search-section">
          {myApprovers.map(approver => (
            <div key={approver._id} className="selected-user-card">
              <div className="role-search-result-content">
                <div className="role-search-result-info">
                  <h5 className="role-search-result-name">{approver.inGameName}</h5>
                  <p className="role-search-result-email">
                    {approver.email} • {approver.points.toLocaleString()} {t('pts', language)}
                  </p>
                  <div className="commission-rates-display">
                    <span className="rate-badge">🎯 {(approver.customCommissionRates?.multiplayer || 0).toFixed(1)}%</span>
                    <span className="rate-badge">👑 {(approver.customCommissionRates?.battleRoyale || 0).toFixed(1)}%</span>
                  </div>
                  <div className="supervisor-earnings text-xs text-gray-400 mt-1">
                    <p>📊 You earn: {((commissionRates.multiplayer?.approver || 25) - (approver.customCommissionRates?.multiplayer || 0)).toFixed(1)}% MP, {((commissionRates.battleRoyale?.approver || 12.5) - (approver.customCommissionRates?.battleRoyale || 0)).toFixed(1)}% BR</p>
                  </div>
                </div>
                <button
                  onClick={() => setConfirmations(prev => ({ ...prev, removeApprover: { show: true, approver } }))}
                  className="btn-compact btn-danger"
                >
                  <UserMinus className="w-4 h-4" />
                  {t('remove', language)}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

export default UsersTab;
